import type { ComponentType } from 'react';
import type { TemplateFeatures } from '@/constants/templateConfig';
import { DEFAULT_BACKGROUND_IMAGE_URL } from '@/constants/templateConfig';
import { TemplateType, type BootData, type CheckoutTheme } from '@/types/boot';
import type { AvailablePaymentMethod, CheckoutData, PaymentMethod } from '@/types/checkout';
import type { PollingStatus } from '@/types/order';
import type { DeviceType, PaymentMethodEnum } from '@/types/common';
import type { Template } from '@/types/template';
import type { Viewport } from '@/utils/template';
import { isApplePayTabMode } from '@/utils/location';

/** Props the registry passes to every template view. */
export interface TemplateViewProps {
  statusOrderPolling: PollingStatus;
}

/** A React component that renders a template's full-screen view. */
export type TemplateView = ComponentType<TemplateViewProps>;

/**
 * Inputs available to a template during boot, *before* the template's view
 * has rendered. Used by `isEligible`, `validatePreRender`, and
 * `filterAvailablePaymentMethods`.
 *
 * `isMobile` is width-based (`isMobileDevice()` → `width < 700`) and is
 * `false` for a phone in landscape. `isLandscape` covers the phone landscape
 * band (700 < w <= 1023, h <= 500) so templates that are mobile-first but
 * also need to run on a rotated phone (PREFERRED FTD / last-payment) can
 * opt in via `ctx.isMobile || ctx.isLandscape` without re-deriving the
 * same predicate from `window` at boot.
 */
export interface TemplateInitContext {
  device: DeviceType;
  isMobile: boolean;
  isLandscape: boolean;
  bootData?: BootData;
  checkoutData?: CheckoutData;
  paymentMethods?: PaymentMethod[];
}

/**
 * Inputs available when picking the default payment method for an active
 * template. `paymentMethods` is the post-filter list that the user will
 * actually see.
 */
export interface PaymentMethodSelectionContext {
  device: DeviceType;
  paymentMethods: AvailablePaymentMethod[];
  bootData?: BootData;
  checkoutData?: CheckoutData;
}

/**
 * A checkout template: a full-screen visual variant of the checkout plus
 * the rules that decide when it runs, which payment methods it shows, and
 * which one is selected by default.
 *
 * To add a new template:
 *   1. Add a value to {@link TemplateType}.
 *   2. Add a TemplateFeatures entry in `src/constants/templateConfig.ts`.
 *   3. Subclass `CheckoutTemplate`, point `View` at a component under
 *      `src/templateViews/`, implement `isEligible` and
 *      `resolveDefaultPaymentMethod`, and override
 *      `validatePreRender` / `filterAvailablePaymentMethods` if needed.
 *   4. Register it in `TemplateRegistry`.
 *
 * Leaf components MUST NOT branch on `template.type === X`; branch on a
 * feature flag in {@link TemplateFeatures} instead so new templates pick
 * up behavior automatically.
 */
export abstract class CheckoutTemplate {
  /** Discriminator persisted in boot/checkout configuration. */
  abstract readonly type: TemplateType;

  /**
   * Template to activate when this one cannot run: boot eligibility fails,
   * the express wallet is unavailable, or the shopper taps "Use other
   * payment method". DEFAULT is terminal — every other template's chain
   * ends there.
   */
  readonly fallbackTemplateType: TemplateType = TemplateType.DEFAULT;

  /** React view rendered by `TemplateRenderer` when this template is active. */
  abstract readonly View: TemplateView;

  /** Feature flags consumed by leaf components. */
  abstract readonly features: TemplateFeatures;

  /**
   * Whether this template can be used in the current init context.
   * Returning `false` makes the registry transparently fall back to
   * the DEFAULT template (no error surfaced to the user).
   */
  abstract isEligible(ctx: TemplateInitContext): boolean;

  /**
   * Default payment method to highlight when this template is active.
   * Return `undefined` to let downstream code decide.
   */
  abstract resolveDefaultPaymentMethod(ctx: PaymentMethodSelectionContext): PaymentMethodEnum | undefined;

  /**
   * Hard pre-render requirement check. Returning a translation key
   * surfaces that error to the user and aborts checkout boot.
   *
   * Use this for "configuration says X but X isn't actually available"
   * cases where falling back silently would mislead the user
   * (e.g. SINGLE_PAYMENT_METHOD with no available method).
   *
   * Defaults to no validation.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  validatePreRender(_ctx: TemplateInitContext): string | null {
    return null;
  }

  /**
   * Optional template-specific filter applied to the BE-provided payment
   * methods, before the global `availablePaymentMethods` config allow-list.
   * Defaults to identity.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  filterAvailablePaymentMethods(paymentMethods: PaymentMethod[], _ctx: TemplateInitContext): PaymentMethod[] {
    return paymentMethods;
  }

  /**
   * Runtime fallback: invoked by express-wallet components (Apple Pay /
   * Google Pay, both Stripe and Adyen) when the native sheet reports it
   * is not available on the current device (`onReady(false)` or load
   * error). Return a {@link Template} to swap into; return `null` to
   * stay on the current template.
   *
   * Distinct from {@link isEligible}, which is checked once at boot and
   * cannot observe live capability. Wallet readiness is fundamentally
   * runtime — and `canMakePayments()` is too unreliable to gate boot on,
   * so we let the wallet itself report and fall back here.
   *
   * Default: no fallback. Override in templates that *require* a working
   * express wallet (e.g. PREFERRED_PAYMENT_METHOD).
   */
  onExpressWalletUnavailable(): Template | null {
    return null;
  }

  /**
   * Should the publisher hero background actually paint behind this
   * template's Details surface at `viewport`?
   *
   * Single decision point that combines:
   *  1. the template's per-viewport opt-in (`features.showBackground`), and
   *  2. whether the publisher has a usable image configured at that viewport
   *     (mobile prefers `mobileBgImage`, falls back to `backgroundImage`).
   *
   * Lives on the class — not as a `template.type === X` branch in leaves and
   * not as a free-standing util — so every consumer (image paint, text auto-
   * contrast, skeleton dispatch, XButton tint…) asks the template itself and
   * gets the same answer. Subclasses can override if they ever need imperative
   * logic, but in practice the data-driven default is enough for all current
   * templates.
   */
  shouldShowBackground(viewport: Viewport, theme?: CheckoutTheme): boolean {
    // The Apple Pay fallback tab is its own Figma screen: a plain white review
    // card. It borrows the SINGLE template only for the layout, so the
    // publisher hero is dropped here — where the paint, the text
    // auto-contrast, the XButton tint and the skeleton all ask the same
    // question — rather than in each of them.
    if (isApplePayTabMode()) return false;
    if (!this.features.showBackground[viewport]) return false;
    const image = viewport === 'mobile' ? theme?.mobileBgImage || theme?.backgroundImage : theme?.backgroundImage;
    // The BE default placeholder isn't a real publisher asset — treat it as no
    // image so the hero isn't painted and text auto-contrasts to black.
    if (!image || image === DEFAULT_BACKGROUND_IMAGE_URL) return false;
    return true;
  }
}
